import React, { useState, useRef, useEffect } from 'react';
import {
  LogOut, Plus, ChevronDown, ChevronRight,
  Presentation, PenTool, FileText, BarChart3, Table2,
  FolderOpen, Palette, User,
  PanelLeftClose, PanelLeft, Clock
} from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from './theme-provider';
import { trialDaysLeftLabel } from '../utils/usageMessages';
import { Settings as SettingsIcon, Briefcase } from 'lucide-react';
import SettingsModal from './SettingsModal';
import { SHOW_SHEETS } from '../utils/features';

const CREATE_OPTIONS = [
  { id: 'presentation', label: 'Presentation', icon: Presentation, color: 'text-green-500' },
  { id: 'proposal', label: 'Proposal', icon: Briefcase, color: 'text-rose-500' },
  { id: 'infographic', label: 'Infographic', icon: PenTool, color: 'text-purple-500' },
  { id: 'document', label: 'Document', icon: FileText, color: 'text-blue-500' },
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3, color: 'text-amber-500' },
  { id: 'sheets', label: 'Sheets', icon: Table2, color: 'text-teal-500' },
];

// Single flat nav group. Home lives on the wizardly.ai wordmark (and the
// logo mark when collapsed); the AI Consultant is reachable from the home
// banner, so neither needs a nav row.
const NAV_ITEMS = [
  { id: 'recents', label: 'Recents', icon: Clock },
  { id: 'projects-list', label: 'Projects', icon: FolderOpen },
  { id: 'brand-identities', label: 'Brand Identities', icon: Palette },
  // r150: Team moved into Settings → Plan & credits — not a navigation destination.
];

// Per-type icons for the recents list; type comes from App.js project mapping.
const RECENT_TYPE_ICONS = {
  'create-ppt': Presentation,
  'native-ppt': Presentation,
  redesign: Presentation,
  infographic: PenTool,
  document: FileText,
  dashboard: BarChart3,
  sheets: Table2,
};

const MIN_SIDEBAR_W = 200;
const MAX_SIDEBAR_W = 340;
const SNAP_COLLAPSE_X = 130;

export function AppSidebar({
  currentPage,
  collapsed,
  onToggleCollapse,
  onNavigate,
  onLogout,
  onStartPresentation,
  onStartProposal,
  onStartInfographic,
  onStartDocument,
  onStartDashboard,
  onStartSheets,
  onStartPlanning,
  userId,
  userName = null,
  userEmail = null,
  recentProjects = [],
  onProjectSelect,
  onOpenFolder,
  usageData = null,
  settingsOpen: settingsOpenProp,
  onSettingsOpenChange,
  hidden = false,
}) {
  const [createOpen, setCreateOpen] = useState(false);
  // Project folders shown inline (the sidebar redesign: sections instead of
  // nav buttons). Fetched once per user; the Projects page owns mutations.
  const [folders, setFolders] = useState([]);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    import('../utils/api').then(({ withBackend }) =>
      fetch(withBackend(`/api/project-folders?user_id=${encodeURIComponent(userId)}`))
        .then(r => (r.ok ? r.json() : { projects: [] }))
        .then(d => { if (!cancelled) setFolders(d.projects || []); })
        .catch(() => { /* sidebar folders are best-effort */ })
    );
    return () => { cancelled = true; };
  }, [userId]);
  const createRef = useRef(null);
  const { theme, toggleTheme } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // r113b: controlled OR uncontrolled. The sidebar still owns this by
  // default, but App can drive it so the retired /profile URL can land
  // someone on Settings -> Account instead of a duplicate page.
  const [settingsOpenLocal, setSettingsOpenLocal] = useState(false);
  const [settingsSection, setSettingsSection] = useState('account');
  const settingsOpen = settingsOpenProp !== undefined ? settingsOpenProp : settingsOpenLocal;
  const setSettingsOpen = onSettingsOpenChange || setSettingsOpenLocal;
  const userMenuRef = useRef(null);
  // Dismiss hides the usage card for the rest of the browser session only.
  const [usageCardDismissed, setUsageCardDismissed] = useState(() => {
    try { return sessionStorage.getItem('wizardly_usage_card_dismissed') === '1'; } catch { return false; }
  });
  const dismissUsageCard = () => {
    setUsageCardDismissed(true);
    try { sessionStorage.setItem('wizardly_usage_card_dismissed', '1'); } catch { /* non-fatal */ }
  };
  // /api/usage/status keys buckets by model tier (flash/pro/opus); Flash is
  // the workhorse tier, so the card tracks it. Presentations count in slides.
  const presUsage = usageData?.usage?.presentation?.flash || {};

  // Close the user menu on outside click (same pattern as the create dropdown)
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const v = parseInt(localStorage.getItem('prezlab_sidebar_width') || '240', 10);
    return Number.isFinite(v) ? Math.min(MAX_SIDEBAR_W, Math.max(MIN_SIDEBAR_W, v)) : 240;
  });
  const [dragging, setDragging] = useState(false);

  // Main content offsets itself with margin-left: var(--sidebar-w) (see
  // .ml-sidebar in App.css), so the drag stays a two-file concern.
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', `${collapsed ? 68 : sidebarWidth}px`);
  }, [collapsed, sidebarWidth]);

  // Drag-to-resize on the right edge. Dragging past the snap threshold toggles
  // collapse instead of leaving a uselessly narrow rail; from the collapsed
  // rail, dragging outward re-expands.
  const startResize = (e) => {
    e.preventDefault();
    setDragging(true);
    document.body.classList.add('sidebar-dragging');
    let done = false;
    const end = () => {
      if (done) return;
      done = true;
      setDragging(false);
      document.body.classList.remove('sidebar-dragging');
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    const onMove = (ev) => {
      const x = ev.clientX;
      if (collapsed) {
        if (x > SNAP_COLLAPSE_X) { onToggleCollapse?.(); end(); }
        return;
      }
      if (x < SNAP_COLLAPSE_X) { onToggleCollapse?.(); end(); return; }
      const w = Math.min(MAX_SIDEBAR_W, Math.max(MIN_SIDEBAR_W, x));
      setSidebarWidth(w);
      localStorage.setItem('prezlab_sidebar_width', String(w));
    };
    const onUp = () => end();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handlers = {
    presentation: onStartPresentation,
    proposal: onStartProposal,
    infographic: onStartInfographic,
    document: onStartDocument,
    dashboard: onStartDashboard,
    sheets: onStartSheets,
  };

  // Close create dropdown on outside click
  useEffect(() => {
    if (!createOpen) return;
    const handleClick = (e) => {
      if (createRef.current && !createRef.current.contains(e.target)) setCreateOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [createOpen]);

  if (hidden) return null;

  return (
    <aside
      style={{ width: collapsed ? 68 : sidebarWidth }}
      className={`fixed top-0 left-0 h-screen bg-sidebar border-r border-sidebar-border flex flex-col z-50 ${
        dragging ? '' : 'transition-all duration-300 ease-in-out'
      }`}
    >
      {/* Logo + collapse toggle. The wordmark IS the Home button (there is no
          separate Home nav row); collapsed mode stacks a logo mark above the
          toggle so Home stays reachable from the rail. */}
      {/* Collapsed header is EXACTLY 60px tall (h-[60px], not min-h) so its
          bottom border stays level with the adjacent chat panel's header —
          the old stacked 32px buttons overflowed the row and knocked the
          two dividers out of alignment. */}
      <div className={`flex border-b border-sidebar-border ${collapsed ? 'flex-col items-center justify-center gap-0.5 h-[60px] px-0' : 'items-center gap-3 px-4 min-h-[60px]'}`}>
        {collapsed ? (
          <button
            onClick={() => onNavigate('dashboard')}
            className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-sidebar-accent/50 transition-colors"
            title="Home"
            aria-label="Home"
          >
            <img src="/logo/wizardly-short.svg" alt="Wizardly" className="w-5 h-5 object-contain" />
          </button>
        ) : (
          <button
            onClick={() => onNavigate('dashboard')}
            className="flex items-center gap-3 min-w-0 flex-1"
            title="Home"
          >
            <span className="text-lg font-bold text-sidebar-foreground truncate">
              wizardly.ai
            </span>
          </button>
        )}
        <button
          onClick={onToggleCollapse}
          className={`rounded-md flex items-center justify-center text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors flex-shrink-0 ${collapsed ? 'w-7 h-5' : 'w-8 h-8'}`}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeft className="w-3.5 h-3.5" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      {/* Create Button */}
      <div className="px-3 pt-4 pb-2" ref={createRef}>
        <div className="relative">
          <button
            onClick={() => setCreateOpen(!createOpen)}
            className={`w-full btn-gradient rounded-xl font-medium text-sm flex items-center gap-2 transition-all ${
              collapsed ? 'justify-center px-0 py-2.5' : 'px-4 py-2.5'
            }`}
          >
            <Plus className="w-4 h-4 flex-shrink-0" />
            {!collapsed && (
              <>
                <span>Create</span>
                <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${createOpen ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>

          {createOpen && (
            <div className={`absolute top-full mt-1.5 bg-card border border-border rounded-xl shadow-xl py-1.5 z-50 animate-fade-in ${
              collapsed ? 'left-full ml-2 w-48' : 'left-0 right-0'
            }`}>
              {CREATE_OPTIONS.filter(opt => SHOW_SHEETS || opt.id !== 'sheets').map(opt => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    onClick={() => { setCreateOpen(false); handlers[opt.id]?.(); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-primary/5 transition-colors text-left"
                  >
                    <Icon className={`w-4 h-4 ${opt.color}`} />
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Navigation. Expanded mode renders SECTIONS (Projects folders +
          Recently Created inline, each with See all) instead of nav rows;
          the collapsed rail keeps the icon shortcuts. All rows are 14px. */}
      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
        {collapsed && <div className="mx-3 my-2 border-t border-sidebar-border" />}
        {(collapsed ? NAV_ITEMS : NAV_ITEMS.filter(i => i.id === 'brand-identities')).map(item => {
          const Icon = item.icon;
          const isActive = currentPage === item.id;

          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={`w-full flex items-center gap-3 rounded-lg text-[14px] font-medium transition-all ${
                collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2'
              } ${
                isActive
                  ? 'bg-sidebar-accent text-sidebar-primary shadow-[inset_2px_0_0_hsl(var(--sidebar-primary))]'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}

        {/* Projects (folders) */}
        {!collapsed && (
          <>
            <div className="flex items-baseline justify-between px-3 mt-5 mb-1">
              <span className="text-[13px] text-muted-foreground">Projects</span>
              <button
                onClick={() => onNavigate('projects-list')}
                className="text-[12px] text-sidebar-primary hover:underline"
              >
                See all
              </button>
            </div>
            {folders.length === 0 && (
              <p className="px-3 py-1 text-[12px] text-sidebar-foreground/50">
                Group work into project folders
              </p>
            )}
            {folders.slice(0, 5).map(f => (
              <button
                key={f.id}
                onClick={() => (onOpenFolder ? onOpenFolder(f.id) : onNavigate('projects-list'))}
                className="w-full flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[14px] text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-all text-left"
                title={f.name || 'Untitled project'}
              >
                <FolderOpen className="w-4 h-4 flex-shrink-0 opacity-60" />
                <span className="truncate">{f.name || 'Untitled project'}</span>
              </button>
            ))}
          </>
        )}

        {/* Recently Created */}
        {!collapsed && (
          <>
            <div className="flex items-baseline justify-between px-3 mt-5 mb-1">
              <span className="text-[13px] text-muted-foreground">Recently Created</span>
              <button
                onClick={() => onNavigate('recents')}
                className="text-[12px] text-sidebar-primary hover:underline"
              >
                See all
              </button>
            </div>
            {recentProjects.length === 0 && (
              <p className="px-3 py-1 text-[12px] text-sidebar-foreground/50">
                Your recent projects will appear here
              </p>
            )}
            {recentProjects.slice(0, 6).map(p => {
              const TypeIcon = RECENT_TYPE_ICONS[p.type] || Clock;
              return (
                <button
                  key={p.id}
                  onClick={() => onProjectSelect?.(p.id)}
                  className="w-full flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[14px] text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-all text-left"
                  title={p.name || 'Untitled'}
                >
                  <TypeIcon className="w-4 h-4 flex-shrink-0 opacity-60" />
                  <span className="truncate">{p.name || 'Untitled'}</span>
                </button>
              );
            })}
          </>
        )}
      </nav>

      {/* Bottom section: usage card + user card (account menu) */}
      <div className="px-3 py-3 border-t border-sidebar-border space-y-2">
        {/* Usage / plan card. Round 86: shown for EVERY tier, prezlab
            included — an unlimited account still deserves a visible meter,
            and month usage is what makes it informative. Credits (from
            /api/usage/status, no extra request) when billing is live;
            the legacy Flash-quota bar as the fallback. */}
        {!collapsed && usageData && !usageCardDismissed && (
          <div className="rounded-xl border border-sidebar-border bg-sidebar-accent/50 px-3 py-2.5">
            {usageData.trial_active ? (
              <>
                <span className="block text-xs font-semibold text-sidebar-primary">
                  {trialDaysLeftLabel(usageData.trial_ends_at)}
                </span>
                <span className="block text-[11px] text-sidebar-foreground/60 mt-0.5">
                  Full access · Opus decks capped at 16 slides
                </span>
              </>
            ) : (
              <>
                <span className="block text-xs font-semibold text-sidebar-foreground capitalize">
                  {/* 'pro' is a legacy alias of Business on the backend — keep the label consistent with UserProfile */}
                  {(usageData.tier === 'pro' ? 'Business' : usageData.tier) || 'Free'} plan
                </span>
                {usageData.credits?.exempt ? (
                  <span className="block text-[11px] text-sidebar-foreground/60 mt-0.5">
                    Unlimited{typeof usageData.credits.month_usage === 'number'
                      ? ` · ${usageData.credits.month_usage.toLocaleString('en-US')} credits used this month`
                      : ''}
                  </span>
                ) : typeof usageData.credits?.balance === 'number' ? (
                  <>
                    <span className="block text-[11px] text-sidebar-foreground/60 mt-0.5">
                      {usageData.credits.balance.toLocaleString('en-US')}
                      {usageData.credits.monthly ? ` of ${usageData.credits.monthly.toLocaleString('en-US')}` : ''} credits left
                    </span>
                    {usageData.credits.monthly > 0 && (
                      <div className="mt-2 h-1.5 rounded-full bg-sidebar-border overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            usageData.credits.balance / usageData.credits.monthly <= 0.15
                              ? 'bg-amber-500' : 'bg-sidebar-primary'}`}
                          style={{ width: `${Math.min(100, Math.max(0, (usageData.credits.balance / usageData.credits.monthly) * 100))}%` }}
                        />
                      </div>
                    )}
                  </>
                ) : presUsage.limit != null && presUsage.limit > 0 ? (
                  <>
                    <span className="block text-[11px] text-sidebar-foreground/60 mt-0.5">
                      {presUsage.used || 0} of {presUsage.limit} Flash {presUsage.unit === 'slides' ? 'slides' : 'presentations'} this month
                    </span>
                    <div className="mt-2 h-1.5 rounded-full bg-sidebar-border overflow-hidden">
                      <div
                        className="h-full rounded-full bg-sidebar-primary transition-all"
                        style={{ width: `${Math.min(100, ((presUsage.used || 0) / presUsage.limit) * 100)}%` }}
                      />
                    </div>
                  </>
                ) : (
                  <span className="block text-[11px] text-sidebar-foreground/60 mt-0.5">
                    View usage & plan details
                  </span>
                )}
              </>
            )}
            <div className="mt-2 flex items-center gap-3">
              <button
                onClick={dismissUsageCard}
                className="text-[11px] text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={() => { setSettingsSection('billing'); setSettingsOpen(true); }}
                className="text-[11px] font-semibold text-sidebar-primary hover:underline"
              >
                {usageData.credits?.exempt ? 'View details' : 'Upgrade plan'}
              </button>
            </div>
          </div>
        )}

        {/* User card — opens a menu with Account, theme, and logout */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(o => !o)}
            className={`w-full flex items-center rounded-lg transition-all hover:bg-sidebar-accent/50 ${
              collapsed ? 'justify-center px-0 py-2' : 'gap-2.5 px-2 py-2'
            }`}
            title={collapsed ? (userName || 'Account') : undefined}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
              {(userName || userEmail || '?')[0].toUpperCase()}
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-semibold text-sidebar-foreground truncate">{userName || 'Account'}</p>
                  {userEmail && <p className="text-[11px] text-sidebar-foreground/60 truncate">{userEmail}</p>}
                </div>
                <ChevronRight className={`w-3.5 h-3.5 text-sidebar-foreground/50 flex-shrink-0 transition-transform ${userMenuOpen ? 'rotate-90' : ''}`} />
              </>
            )}
          </button>

          {userMenuOpen && (
            <div className={`absolute bg-card border border-border rounded-xl shadow-xl py-1.5 z-50 animate-fade-in ${
              collapsed ? 'left-full ml-2 bottom-0 w-48' : 'bottom-full mb-2 left-0 right-0'
            }`}>
              <button
                onClick={() => { setUserMenuOpen(false); setSettingsSection('account'); setSettingsOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-primary/5 transition-colors text-left"
              >
                <User className="w-4 h-4" />
                Account
              </button>
              <button
                onClick={() => { setUserMenuOpen(false); setSettingsSection('account'); setSettingsOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-primary/5 transition-colors text-left"
              >
                <SettingsIcon className="w-4 h-4" />
                Settings
              </button>
              <button
                onClick={() => { toggleTheme(); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-primary/5 transition-colors text-left"
              >
                <span className="material-symbols-outlined text-[17px]">
                  {theme === 'dark' ? 'light_mode' : 'dark_mode'}
                </span>
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </button>
              <div className="mx-3 my-1 border-t border-border" />
              <button
                onClick={() => { setUserMenuOpen(false); onLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground hover:bg-destructive/10 hover:text-destructive transition-colors text-left"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Resize handle — drag to resize, double-click to collapse/expand */}
      <div
        onMouseDown={startResize}
        onDoubleClick={() => onToggleCollapse?.()}
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-sidebar-primary/30 active:bg-sidebar-primary/50 transition-colors"
        title="Drag to resize"
      />
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)}
                     usageData={usageData} initialSection={settingsSection} />
    </aside>
  );
}
